import { DIMENSION_KEYS, type DimensionKey } from '../types';
import type { SpeciesQuizPair } from './parseSpeciesQuizCsv';
import { importanceWeight, type ImportanceLevel } from './importanceWeights';

export type DimensionPreference = {
  detailLevel: number | null;
  importance: ImportanceLevel | null;
};

export type UserMatchProfile = Record<DimensionKey, DimensionPreference>;

/** Profile shape required by `POST /match` (all dimensions, 1–5 + importance). */
export type CompletedUserMatchProfile = Record<
  DimensionKey,
  { detailLevel: number; importance: ImportanceLevel }
>;

const HIDDEN_DIMENSION_DEFAULT: CompletedUserMatchProfile[DimensionKey] = {
  detailLevel: 3,
  importance: 'not_very',
};

export function defaultUserMatchProfile(): UserMatchProfile {
  return DIMENSION_KEYS.reduce((acc, key) => {
    acc[key] = { detailLevel: null, importance: null };
    return acc;
  }, {} as UserMatchProfile);
}

/** Wizard step is complete when both answers are set and detail matches a quiz option. */
export function isQuizStepComplete(
  pair: SpeciesQuizPair,
  profile: UserMatchProfile,
): boolean {
  const dim = profile[pair.dimensionKey];
  if (dim.detailLevel == null || dim.importance == null) return false;
  return pair.detailOptions.some((opt) => opt.level === dim.detailLevel);
}

/**
 * Builds the API profile: quiz dimensions from user answers; dimensions not in
 * `pairs` (e.g. `housemanners` when the CSV has 6 pairs) use defaults so the
 * backend schema stays satisfied.
 */
export function buildMatchApiProfile(
  profile: UserMatchProfile,
  pairs: SpeciesQuizPair[],
): CompletedUserMatchProfile | null {
  if (!pairs.length) return null;
  for (const pair of pairs) {
    if (!isQuizStepComplete(pair, profile)) return null;
  }

  const keysInQuiz = new Set(pairs.map((p) => p.dimensionKey));
  const out = {} as CompletedUserMatchProfile;

  for (const key of DIMENSION_KEYS) {
    if (keysInQuiz.has(key)) {
      const d = profile[key];
      if (d.detailLevel == null || d.importance == null) return null;
      const pair = pairs.find((p) => p.dimensionKey === key);
      if (!pair?.detailOptions.some((o) => o.level === d.detailLevel))
        return null;
      out[key] = { detailLevel: d.detailLevel, importance: d.importance };
    } else {
      const d = profile[key];
      if (d.detailLevel != null && d.importance != null) {
        out[key] = { detailLevel: d.detailLevel, importance: d.importance };
      } else {
        out[key] = HIDDEN_DIMENSION_DEFAULT;
      }
    }
  }
  return out;
}

/** Flat 1–5 vector for comparison UI / legacy helpers. */
export function profileToDetailVector(
  profile: UserMatchProfile,
): Record<DimensionKey, number> {
  return DIMENSION_KEYS.reduce(
    (acc, key) => {
      acc[key] = profile[key].detailLevel ?? 0;
      return acc;
    },
    {} as Record<DimensionKey, number>,
  );
}

/** Shape of `preferences` in POST /match and matching-poc.response.v2 (for UI preview only). */
export function profileToSerializable(
  profile: UserMatchProfile,
): Record<
  DimensionKey,
  {
    detailLevel: number | null;
    importance: ImportanceLevel | null;
    weight: number | null;
  }
> {
  return DIMENSION_KEYS.reduce(
    (acc, key) => {
      const p = profile[key];
      const complete = p.detailLevel != null && p.importance != null;
      acc[key] = {
        detailLevel: p.detailLevel,
        importance: p.importance,
        weight:
          complete && p.importance != null
            ? importanceWeight(p.importance)
            : null,
      };
      return acc;
    },
    {} as Record<
      DimensionKey,
      {
        detailLevel: number | null;
        importance: ImportanceLevel | null;
        weight: number | null;
      }
    >,
  );
}
